import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  CircularProgress
} from '@mui/material';
import { useTeams, useUsers, useAuth, useNotification } from '../../hooks';
import { useTranslation } from 'react-i18next';
import { hasuraClient } from '../../utils/graphqlClient';
import {
  CREATE_JOB_ASSIGNMENT,
  UPDATE_JOB_ASSIGNMENT,
  ASSIGN_FIRM_TO_USER_MUTATION,
  FULFILL_FINANCIAL_REQUESTS_FOR_JOB,
} from '../../graphql/mutations';

const JobAssignmentModal = ({ open, onClose, selectedJob, onSuccess }) => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const { t } = useTranslation();
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [techTeamId, setTechTeamId] = useState('');
  const [techUserId, setTechUserId] = useState('');

  const [assignFinancial, setAssignFinancial] = useState(false);
  const [finTeamId, setFinTeamId] = useState('');
  const [finUserId, setFinUserId] = useState('');

  const scope = selectedJob?.consultingScope || 'MALI_TEKNIK';
  const isApproved = selectedJob?.job_status === 'Approved' || selectedJob?.job_status === 'ONAYLANDI' || selectedJob?.jobStatus === 'ONAYLANDI';
  const showTechnical = scope !== 'SADECE_MALI';
  const showFinancial = true; // always shown — mandatory or optional depending on scope
  const finMandatory = scope === 'SADECE_MALI' || (scope === 'MALI_TEKNIK' && isApproved);

  // Dropdown data from hooks for the modal
  const { teams: allTeams = [], isLoading: teamsLoading } = useTeams();
  const { users = [], isLoading: usersLoading } = useUsers();

  // Isten ayrilmis kullanicilar (users.work_end_date DOLU) atama dropdown'inda
  // gozukmesin — Buket ornegindeki gibi tarihi gecmis ayrilislar hala listede
  // gorunuyordu. Duzenleme senaryosunda daha once atanmis ayrilmis bir kullanici
  // varsa yine listede tutulur ki secili gorunsun (asagida spread).
  const isActiveUser = (u) => {
    const end = u?.workEndDate || u?.work_end_date;
    if (!end) return true;
    // date veya string olabilir; guvenli parse.
    const d = end instanceof Date ? end : new Date(end);
    if (Number.isNaN(d.getTime())) return true;
    return d.getTime() > Date.now();
  };

  // Etiketlere göre filtre: teknik etiketli ekipler Teknik Sorumlu'da, mali etiketli ekipler Mali Sorumlu'da
  const teamHasTag = (team, tagName) =>
    team?.team_tags?.some(tt => (tt.tag?.name || '').toLowerCase() === String(tagName).toLowerCase());
  const technicalTeamsBase = allTeams.filter(t => teamHasTag(t, 'teknik'));
  const financialTeamsBase = allTeams.filter(t => teamHasTag(t, 'mali'));
  // Düzenlemede seçili ekip listede yoksa ekle (önceden atanmış ekipler için)
  const selectedTechTeam = techTeamId ? allTeams.find(t => t.id === parseInt(techTeamId)) : null;
  const selectedFinTeam = finTeamId ? allTeams.find(t => t.id === parseInt(finTeamId)) : null;
  const technicalTeams = selectedTechTeam && !technicalTeamsBase.some(t => t.id === selectedTechTeam.id)
    ? [...technicalTeamsBase, selectedTechTeam]
    : technicalTeamsBase;
  const financialTeams = selectedFinTeam && !financialTeamsBase.some(t => t.id === selectedFinTeam.id)
    ? [...financialTeamsBase, selectedFinTeam]
    : financialTeamsBase;

  useEffect(() => {
    if (open && selectedJob) {
      const jobScope = selectedJob.consultingScope || 'MALI_TEKNIK';
      const jobApproved = selectedJob.job_status === 'Approved' || selectedJob.job_status === 'ONAYLANDI' || selectedJob.jobStatus === 'ONAYLANDI';
      const isMandatory = jobScope === 'SADECE_MALI' || (jobScope === 'MALI_TEKNIK' && jobApproved);

      if (selectedJob.assignmentData) {
        setTechTeamId(selectedJob.assignmentData.technical_team_id || selectedJob.assignmentData.technicalTeamId || '');
        setTechUserId(selectedJob.assignmentData.technical_user_id || selectedJob.assignmentData.technicalUserId || '');

        const hasFin = !!(selectedJob.assignmentData.financial_team_id || selectedJob.assignmentData.financialTeamId);
        setAssignFinancial(isMandatory || hasFin);
        setFinTeamId(selectedJob.assignmentData.financial_team_id || selectedJob.assignmentData.financialTeamId || '');
        setFinUserId(selectedJob.assignmentData.financial_user_id || selectedJob.assignmentData.financialUserId || '');
      } else {
        setTechTeamId('');
        setTechUserId('');
        setAssignFinancial(isMandatory);
        setFinTeamId('');
        setFinUserId('');
      }
    }
  }, [open, selectedJob]);

  const handleSubmitAssignment = async () => {
    if (!selectedJob) return;

    const hasTechnical = !!(techTeamId && techUserId);
    const hasFinancial = !!(assignFinancial && finTeamId && finUserId);

    if (showTechnical && !hasTechnical) {
      showNotification(t('jobAssignments.modals.validation.techRequired', 'Teknik Ekip ve Teknik Üye seçimi zorunludur.'), 'error');
      return;
    }

    if ((finMandatory || assignFinancial) && (!finTeamId || !finUserId)) {
      showNotification(t('jobAssignments.modals.validation.finRequired', 'Mali Ekip ve Mali Üye seçimi zorunludur.'), 'error');
      return;
    }

    if (!hasTechnical && !hasFinancial) {
      showNotification(
        t('jobAssignments.modals.validation.responsibleRequired', 'En az teknik veya mali sorumlu seçimi yapmalısınız.'),
        'error'
      );
      return;
    }
    const jobId = selectedJob.id || selectedJob.job_id || selectedJob.assignmentData?.job_id || selectedJob.assignmentData?.job?.id || selectedJob.job?.id;
    if (!jobId) {
      showNotification(t('jobAssignments.modals.validation.jobIdRequired', 'İş bilgisi bulunamadı. Lütfen sayfayı yenileyip tekrar deneyin.'), 'error');
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = {
        company_id: user.companyId,
        job_id: jobId,
        technical_team_id: techTeamId ? parseInt(techTeamId) : null,
        technical_user_id: techUserId ? parseInt(techUserId) : null,
        financial_team_id: assignFinancial && finTeamId ? parseInt(finTeamId) : null,
        financial_user_id: assignFinancial && finUserId ? parseInt(finUserId) : null,
        assigned_by: user.id
      };

      if (selectedJob.assignment_id) {
        await hasuraClient.mutate(UPDATE_JOB_ASSIGNMENT, {
          id: selectedJob.assignment_id,
          set: payload
        });
      } else {
        const response = await hasuraClient.mutate(CREATE_JOB_ASSIGNMENT, {
          object: payload
        });
        const newAssignmentId = response?.insert_job_assignments_one?.id;
        
        await hasuraClient.mutate(`
          mutation UpdateJob($jobId: uuid!, $assignmentId: Int!) {
            update_jobs_by_pk(pk_columns: {id: $jobId}, _set: {job_assignment_id: $assignmentId}) {
              id
            }
          }
        `, { jobId, assignmentId: newAssignmentId });
      }

      // Mali sorumlu atandıysa, bu iş için bekleyen mali sorumlu taleplerini fulfilled olarak işaretle.
      if (assignFinancial && finUserId) {
        try {
          await hasuraClient.mutate(FULFILL_FINANCIAL_REQUESTS_FOR_JOB, {
            jobId,
            fulfilledBy: user.id,
          });
        } catch (fulfillErr) {
          // Fulfilment hatası atamayı bozmasin — sadece logla.
          console.error('Fulfill financial requests error:', fulfillErr);
        }
      }

      // Auto-assign firm to technical/financial users (only for relevant scope)
      const firmId = selectedJob.assignmentData?.job?.firm?.id || selectedJob.job?.firm?.id;
      if (firmId) {
        const userIds = [
          showTechnical && techUserId ? parseInt(techUserId) : null,
          showFinancial && assignFinancial && finUserId ? parseInt(finUserId) : null,
        ].filter(Boolean);

        await Promise.allSettled(
          userIds.map(uid =>
            hasuraClient.mutate(ASSIGN_FIRM_TO_USER_MUTATION, {
              userId: uid,
              firmId: parseInt(firmId),
              companyId: user.companyId,
            })
          )
        );
      }

      showNotification(t('jobAssignments.modals.success', 'İş başarıyla atandı.'), 'success');
      onSuccess();
      onClose();

    } catch (error) {
      console.error('Submit assignment error:', error);
      showNotification(t('jobAssignments.modals.error', 'Atama işlemi sırasında hata oluştu.'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: '30vh' } }}
    >
      <DialogTitle>
        <Typography variant="h6" component="span" fontWeight="bold">
          {t('jobAssignments.modals.title', 'Sorumlu Ata')}:{' '}
          {[
            selectedJob?.firmName || selectedJob?.firm?.firm_name || selectedJob?.firm?.firm_short_name,
            selectedJob?.job_title || selectedJob?.jobTitle,
          ].filter(Boolean).join(' — ') || '-'}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>

        {showTechnical && (
        <Box sx={{ mb: 4, p: 3, bgcolor: 'primary.50', borderRadius: 2, border: '1px solid', borderColor: 'primary.100' }}>
          <Typography variant="subtitle1" fontWeight="bold" color="primary.main" gutterBottom>
            {t('jobAssignments.modals.techRequired', 'Teknik Sorumlu (Zorunlu)')}
          </Typography>
          <Grid container spacing={3}>
            <Grid>
              <FormControl fullWidth variant="outlined" sx={{ minWidth: 200 }}>
                <InputLabel id="techTeam-label">{t('jobAssignments.modals.selectTechTeam', 'Teknik Ekip Seç')}</InputLabel>
                <Select
                  labelId="techTeam-label"
                  value={techTeamId}
                  onChange={(e) => {
                    setTechTeamId(e.target.value);
                    setTechUserId('');
                  }}
                  label={t('jobAssignments.modals.selectTechTeam', 'Teknik Ekip Seç')}
                  disabled={teamsLoading}
                >
                  {technicalTeams.map((team) => (
                    <MenuItem key={team.id} value={team.id}>{team.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid>
              <FormControl fullWidth variant="outlined" sx={{ minWidth: 200 }}>
                <InputLabel id="techUser-label">{t('jobAssignments.modals.selectTechUser', 'Teknik Üye Seç')}</InputLabel>
                <Select
                  labelId="techUser-label"
                  value={techUserId}
                  onChange={(e) => setTechUserId(e.target.value)}
                  label={t('jobAssignments.modals.selectTechUser', 'Teknik Üye Seç')}
                  disabled={!techTeamId || usersLoading}
                >
                  {users
                    .filter(u => u.teamId === parseInt(techTeamId))
                    // Ayrilmis kullanicilar dropdown'da gizli — ancak zaten
                    // secili olan (edit modu) kullanici ayrilmis dahi olsa
                    // listede kalsin ki secili gorunmeye devam etsin.
                    .filter(u => isActiveUser(u) || u.id === techUserId)
                    .map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      {u.first_name || u.firstName} {u.last_name || u.lastName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
        )}

        {showFinancial && (
        <Box sx={{ p: 3, bgcolor: finMandatory ? 'warning.50' : 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: assignFinancial ? 3 : 0 }}>
            <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
              {t('jobAssignments.modals.finTitle', 'Mali Sorumlu')}
            </Typography>
            {finMandatory ? (
              <Typography variant="caption" color="error.main" fontWeight="bold">
                {scope === 'SADECE_MALI'
                  ? t('jobAssignments.modals.finMandatoryScope', 'Zorunlu (Sadece Mali Kapsam)')
                  : t('jobAssignments.modals.finMandatoryApproved', 'Zorunlu (İş Onaylandı)')}
              </Typography>
            ) : (
              <FormControlLabel
                control={
                  <Switch
                    checked={assignFinancial}
                    onChange={(e) => setAssignFinancial(e.target.checked)}
                  />
                }
                label={t('jobAssignments.modals.assignFin', 'Mali Sorumlu Ata')}
              />
            )}
          </Box>

          {finMandatory && scope === 'MALI_TEKNIK' && (
            <Typography variant="caption" color="warning.main" sx={{ display: 'block', mb: 3 }}>
              {t('jobAssignments.modals.finMandatoryNote', '* İş Onaylandı statüsünde olduğu için Mali Sorumlu atanması ZORUNLUDUR.')}
            </Typography>
          )}

          {assignFinancial && (
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth variant="outlined" sx={{ minWidth: 200 }}>
                  <InputLabel id="finTeam-label">{t('jobAssignments.modals.selectFinTeam', 'Mali Ekip Seç')}</InputLabel>
                  <Select
                    labelId="finTeam-label"
                    value={finTeamId}
                    onChange={(e) => {
                      setFinTeamId(e.target.value);
                      setFinUserId(''); 
                    }}
                    label={t('jobAssignments.modals.selectFinTeam', 'Mali Ekip Seç')}
                    disabled={teamsLoading}
                  >
                    {financialTeams.map((team) => (
                      <MenuItem key={team.id} value={team.id}>{team.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth variant="outlined" sx={{ minWidth: 200 }}>
                  <InputLabel id="finUser-label">{t('jobAssignments.modals.selectFinUser', 'Mali Üye Seç')}</InputLabel>
                  <Select
                    labelId="finUser-label"
                    value={finUserId}
                    onChange={(e) => setFinUserId(e.target.value)}
                    label={t('jobAssignments.modals.selectFinUser', 'Mali Üye Seç')}
                    disabled={!finTeamId || usersLoading}
                  >
                    {users
                      .filter(u => u.teamId === parseInt(finTeamId))
                      // Ayrilmis kullanicilar dropdown'da gizli — mevcut secili
                      // (edit modu) kullanici ise korunur.
                      .filter(u => isActiveUser(u) || u.id === finUserId)
                      .map((u) => (
                      <MenuItem key={u.id} value={u.id}>
                        {u.first_name || u.firstName} {u.last_name || u.lastName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          )}
        </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={isSubmitting}>
          {t('common.cancel', 'İptal')}
        </Button>
        <Button 
          onClick={handleSubmitAssignment} 
          variant="contained" 
          color="primary"
          disabled={isSubmitting}
          startIcon={isSubmitting && <CircularProgress size={20} color="inherit" />}
        >
          {t('common.save', 'Ata ve Kaydet')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default JobAssignmentModal;
